/**
 * Etapa 2 do autocadastro — o que cada veículo vai atender (RN-CP-23, D-15).
 *
 * Por veículo: a modalidade (escolhida pelo interessado, entre as do período),
 * uma DRE (entre as do período), de 1 ao teto de escolas do período em ordem
 * de preferência (lista do EOL, já filtrada pela DRE e pela modalidade) e, se
 * quiser, a cooperativa (lista do SGTP).
 *
 * A prioridade sai da ORDEM EM QUE SE MARCA — a primeira marcada é a 1ª opção.
 * Trocar DRE ou modalidade descarta as escolas: elas foram escolhidas para a
 * combinação anterior, e manter faria a inscrição sair com escola que não
 * atende a nova.
 *
 * A modalidade Acessível só aparece para veículo com acessibilidade registrada
 * no SGTP: é pré-requisito da modalidade, e o servidor recusaria (regra 7 de
 * interface — o que não se pode escolher não é oferecido).
 */

import { Car, Check, MapPin, School, Shapes, Users } from 'lucide-react';
import { useId } from 'react';

import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Checkbox } from '@/ui/base/checkbox';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { cn } from '@/nucleo/utilitarios';

import {
  mensagemDoErro,
  useCooperativasDaInscricao,
  useEscolasDaInscricao,
  type CicloDaInscricao,
  type ModalidadeDaInscricao,
  type VeiculoDoPerfil,
  type VeiculoDoRascunho,
} from './apiDaInscricao';
import { ROTULO_DA_MODALIDADE, formatarPlaca } from './contrato';
import { veiculoConfigurado } from './validacaoDoAssistente';

/** Valor do `Select` de cooperativa quando não há nenhuma. */
const SEM_COOPERATIVA = 'sem-cooperativa';

export interface EtapaConfigurarVeiculosProps {
  ciclo: CicloDaInscricao;
  veiculos: VeiculoDoRascunho[];
  /** O retrato do SGTP de cada placa, para modelo, ano e acessibilidade. */
  doSgtp: VeiculoDoPerfil[];
  aoAtualizar: (placa: string, veiculo: VeiculoDoRascunho) => void;
}

export function EtapaConfigurarVeiculos({
  ciclo,
  veiculos,
  doSgtp,
  aoAtualizar,
}: EtapaConfigurarVeiculosProps) {
  const cooperativas = useCooperativasDaInscricao();
  const configurados = veiculos.filter((v) => veiculoConfigurado(v, ciclo.maxEscolas)).length;

  if (veiculos.length === 0) {
    return (
      <EmptyState
        icon={Car}
        title="Nenhum veículo marcado"
        description="Volte à etapa anterior e marque ao menos um veículo para configurar aqui."
      />
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Car aria-hidden="true" className="h-4 w-4" />
        <AlertTitle>
          {configurados} de {veiculos.length} veículo(s) configurado(s)
        </AlertTitle>
        <AlertDescription>
          Cada veículo precisa de modalidade, DRE e de 1 a {ciclo.maxEscolas} escola(s).
        </AlertDescription>
      </Alert>

      {cooperativas.isError && (
        <ErrorState
          variant="load-failed"
          description={mensagemDoErro(
            cooperativas.error,
            'Não foi possível carregar as cooperativas agora. Você pode seguir sem indicar cooperativa.',
          )}
          primaryAction={{ label: 'Tentar de novo', onClick: () => void cooperativas.refetch() }}
        />
      )}

      {veiculos.map((veiculo) => (
        <CartaoDoVeiculo
          key={veiculo.placa}
          ciclo={ciclo}
          veiculo={veiculo}
          doSgtp={doSgtp.find((v) => v.placa === veiculo.placa)}
          cooperativas={cooperativas.data ?? []}
          aoAtualizar={(proximo) => aoAtualizar(veiculo.placa, proximo)}
        />
      ))}
    </div>
  );
}

function CartaoDoVeiculo({
  ciclo,
  veiculo,
  doSgtp,
  cooperativas,
  aoAtualizar,
}: {
  ciclo: CicloDaInscricao;
  veiculo: VeiculoDoRascunho;
  doSgtp: VeiculoDoPerfil | undefined;
  cooperativas: { cnpj: string; nome: string }[];
  aoAtualizar: (veiculo: VeiculoDoRascunho) => void;
}) {
  const idBase = useId();
  const escolas = useEscolasDaInscricao(veiculo.dreId, veiculo.modalidade);
  const completo = veiculoConfigurado(veiculo, ciclo.maxEscolas);
  const modalidades = ciclo.modalidades.filter(
    (m) => m !== 'ACESSIVEL' || doSgtp?.acessivel === true,
  );
  const prontoParaEscolas = Boolean(veiculo.dreId && veiculo.modalidade);

  function alternarEscola(codigo: string) {
    const ja = veiculo.escolas.includes(codigo);
    if (!ja && veiculo.escolas.length >= ciclo.maxEscolas) return;
    aoAtualizar({
      ...veiculo,
      escolas: ja ? veiculo.escolas.filter((c) => c !== codigo) : [...veiculo.escolas, codigo],
    });
  }

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <Car aria-hidden="true" className="h-4 w-4 text-primary" />
            <span className="font-mono">{formatarPlaca(veiculo.placa)}</span>
            {doSgtp && (
              <span className="text-sm font-normal text-muted-foreground">
                {doSgtp.modelo} ({doSgtp.anoFabricacao})
              </span>
            )}
          </CardTitle>
          <Badge
            variant="outline"
            className={cn(
              completo ? 'border-success/30 text-success' : 'border-warning/40 text-foreground',
            )}
          >
            {completo ? 'Configurado' : 'Falta configurar'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idBase}-modalidade`} className="flex items-center gap-1.5">
              <Shapes aria-hidden="true" className="h-3.5 w-3.5" />
              Modalidade
            </Label>
            <Select
              value={veiculo.modalidade ?? undefined}
              onValueChange={(valor) =>
                aoAtualizar({
                  ...veiculo,
                  modalidade: valor as ModalidadeDaInscricao,
                  escolas: [],
                })
              }
            >
              <SelectTrigger id={`${idBase}-modalidade`}>
                <SelectValue placeholder="Selecione a modalidade" />
              </SelectTrigger>
              <SelectContent>
                {modalidades.map((m) => (
                  <SelectItem key={m} value={m}>
                    {ROTULO_DA_MODALIDADE[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${idBase}-dre`} className="flex items-center gap-1.5">
              <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
              DRE
            </Label>
            <Select
              value={veiculo.dreId ?? undefined}
              onValueChange={(valor) => aoAtualizar({ ...veiculo, dreId: valor, escolas: [] })}
            >
              <SelectTrigger id={`${idBase}-dre`}>
                <SelectValue placeholder="Selecione a DRE" />
              </SelectTrigger>
              <SelectContent>
                {ciclo.dres.map((dre) => (
                  <SelectItem key={dre.id} value={dre.id}>
                    {dre.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <School aria-hidden="true" className="h-3.5 w-3.5" />
            Escolas de preferência
          </legend>
          <p className="text-xs text-muted-foreground">
            Marque de 1 a {ciclo.maxEscolas}. A ordem em que você marca vira a ordem de preferência.
          </p>

          {!prontoParaEscolas && (
            <p className="rounded-lg border border-dashed border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              Escolha a modalidade e a DRE acima para ver as escolas.
            </p>
          )}
          {prontoParaEscolas && escolas.isPending && (
            <Spinner size="sm" rotulo="Carregando as escolas da DRE" />
          )}
          {prontoParaEscolas && escolas.isError && (
            <ErrorState
              variant="load-failed"
              description={mensagemDoErro(
                escolas.error,
                'Não foi possível carregar as escolas dessa DRE agora.',
              )}
              primaryAction={{ label: 'Tentar de novo', onClick: () => void escolas.refetch() }}
            />
          )}
          {prontoParaEscolas && escolas.isSuccess && escolas.data.length === 0 && (
            <EmptyState
              icon={School}
              title="Nenhuma escola nesta DRE para esta modalidade"
              description="Escolha outra DRE ou outra modalidade."
            />
          )}
          {prontoParaEscolas && escolas.isSuccess && escolas.data.length > 0 && (
            <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-xl border border-border">
              {escolas.data.map((escola) => {
                const posicao = veiculo.escolas.indexOf(escola.codigoEol);
                const escolhida = posicao >= 0;
                const cheio = veiculo.escolas.length >= ciclo.maxEscolas && !escolhida;
                const id = `${idBase}-escola-${escola.codigoEol}`;
                return (
                  <li key={escola.codigoEol}>
                    <Label
                      htmlFor={id}
                      className={cn(
                        'flex items-start gap-3 p-3 text-sm font-normal',
                        cheio ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                        escolhida && 'bg-primary/5',
                        !escolhida && !cheio && 'hover:bg-muted/40',
                      )}
                    >
                      <Checkbox
                        id={id}
                        checked={escolhida}
                        disabled={cheio}
                        className="mt-0.5"
                        onCheckedChange={() => alternarEscola(escola.codigoEol)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-foreground">{escola.nome}</span>
                        <span className="block text-xs text-muted-foreground">
                          {escola.tipoUnidade} · EOL {escola.codigoEol}
                          {escola.endereco ? ` · ${escola.endereco}` : ''}
                        </span>
                      </span>
                      {escolhida && (
                        <Badge variant="outline" className="shrink-0 gap-1 border-primary/40">
                          <Check aria-hidden="true" className="h-3 w-3" />
                          {posicao + 1}ª opção
                        </Badge>
                      )}
                    </Label>
                  </li>
                );
              })}
            </ul>
          )}
        </fieldset>

        <div className="space-y-2">
          <Label htmlFor={`${idBase}-cooperativa`} className="flex items-center gap-1.5">
            <Users aria-hidden="true" className="h-3.5 w-3.5" />
            Cooperativa <span className="text-muted-foreground">(opcional)</span>
          </Label>
          <Select
            value={veiculo.cooperativaCnpj ?? SEM_COOPERATIVA}
            onValueChange={(valor) =>
              aoAtualizar({
                ...veiculo,
                cooperativaCnpj: valor === SEM_COOPERATIVA ? null : valor,
              })
            }
          >
            <SelectTrigger id={`${idBase}-cooperativa`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SEM_COOPERATIVA}>Sem cooperativa</SelectItem>
              {cooperativas.map((c) => (
                <SelectItem key={c.cnpj} value={c.cnpj}>
                  {c.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {veiculo.cooperativaCnpj && (
            <p className="rounded-lg border border-border bg-muted/30 p-2.5 text-xs text-foreground">
              A cooperativa recebe um aviso por e-mail com o protocolo deste veículo.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default EtapaConfigurarVeiculos;
